import { DetectionEventDatePrecision } from "@/constants/terminal/detection-event-date-precision"

const ISO_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
export const DAY_MS = 86_400_000

// A real calendar day, year-month-day, as whole UTC days since the epoch;
// null for anything else, a timestamp or an impossible date (2026-02-30).
// A calendar day never shifts with the viewer's time zone.
export const parseTerminalDay = (value: string | null | undefined) => {
  if (!value || !ISO_DATE_ONLY_RE.test(value)) return null
  const time = Date.parse(`${value}T00:00:00Z`)
  if (!Number.isFinite(time)) return null
  return new Date(time).toISOString().slice(0, 10) === value
    ? time / DAY_MS
    : null
}

// A date-only string is read as a local calendar day (no UTC day drift); a
// full timestamp is read as the instant it names.
const parseTerminalTimestamp = (timestamp: string): Date => {
  const match = ISO_DATE_ONLY_RE.exec(timestamp)
  if (!match) return new Date(timestamp)

  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

// The one date a buyer sees in Night Shift (Dhanur, 24 Oct 2026): numeric,
// month/day/two-digit year, no leading zeros, like 10/8/26. File dates,
// Activity, Requests, detection cards and tables all read this; the export
// writes year-month-day instead (formatTerminalExportDate).
export const formatTerminalDate = (timestamp: string): string => {
  const date = parseTerminalTimestamp(timestamp)
  const year = String(date.getFullYear()).slice(-2)
  return `${date.getMonth() + 1}/${date.getDate()}/${year}`
}

// A Detection's event day, read at the precision its source gave (WB-1328:
// a licence board dates to the day, some registries only to the month or
// year, a present-state read only to the day it was confirmed):
//   day    10/8/26      month  10/26      year  2026
//   as_of  as of 10/8/26        none   Undated
export const formatTerminalDetectionDate = (
  day: string | null,
  precision: DetectionEventDatePrecision | null = null
): string => {
  if (!day) return "Undated"
  const date = parseTerminalTimestamp(day)
  switch (precision) {
    case DetectionEventDatePrecision.MONTH:
      return `${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`
    case DetectionEventDatePrecision.YEAR:
      return String(date.getFullYear())
    case DetectionEventDatePrecision.AS_OF:
      return `as of ${formatTerminalDate(day)}`
    case DetectionEventDatePrecision.DAY:
    case null:
      return formatTerminalDate(day)
  }
}

// WB-1328: a present-state read ("currently suspended", "not in good
// standing") states no event day, so a Detection whose records state none
// shows the day they were last checked, "as of" that day, as LIVE's
// precision-aware dates do. Null when a record states an event day, or the
// Detection has no records.
export const asOfDateOf = (
  records: { eventDate: string | null; observedDay: string }[]
): string | null =>
  records.length === 0 || records.some(({ eventDate }) => eventDate)
    ? null
    : (records
        .map(({ observedDay }) => observedDay)
        .toSorted()
        .at(-1) ?? null)

// A Detection's dates: its event day, and the day its records were last
// checked, which stands in for an undated one.
export type DetectionDay = { eventDate: string | null; asOfDate: string | null }

// A Detection's date as the buyer reads it: its event day, else the day its
// records were last checked, "as of" that day (WB-1328); null when it has
// neither, which the card reads "Undated" and a table as the absence dash.
export const formatDetectionDay = ({
  eventDate,
  asOfDate,
}: DetectionDay): string | null => {
  if (eventDate) return formatTerminalDetectionDate(eventDate)
  if (asOfDate)
    return formatTerminalDetectionDate(
      asOfDate,
      DetectionEventDatePrecision.AS_OF
    )
  return null
}

// The order a Detection's date sorts in: dated newest first, then "as of"
// newest first, then neither (null, last in either direction).
export const detectionDaySortValue = ({
  eventDate,
  asOfDate,
}: DetectionDay): string | null => {
  if (eventDate) return `1 ${eventDate}`
  if (asOfDate) return `0 ${asOfDate}`
  return null
}

// Dates in Export Files read year-month-day, 2026-10-08, which spreadsheets
// read as a date and which sorts as text (Dhanur, 25 Oct 2026). A timestamp
// reads as its day in the given time zone, as the viewer saw it.
export const formatTerminalExportDate = (
  timestamp: string,
  timeZone?: string
): string =>
  parseTerminalTimestamp(timestamp).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: ISO_DATE_ONLY_RE.test(timestamp) ? undefined : timeZone,
  })
